// Accounts module for Docospot
// Handles account management, hierarchy, and CRUD operations

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { apiCall } from '../core/api.js';
import { showError, showSuccess } from '../core/ui.js';
import { getCurrentUser } from '../utils/helpers.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import * as modals from './accounts-modals.js';

// Store accounts data for searching
let accountsTreeData = null;
let flatAccountsList = [];
let searchTimeout = null;

/**
 * Display the accounts page with hierarchy view
 */
export async function showAccounts() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view accounts');
        return;
    }

    mainContent.innerHTML = `
        <!-- Filter and Search Area -->
        <div class="sticky top-16 z-30 mb-0 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <!-- Row 1: Add Account Button -->
                <div class="flex justify-end mb-2">
                    <button onclick="showCreateAccountModal()" 
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        ${t('accounts.addButton')}
                    </button>
                </div>
                
                <!-- Row 2: Search and Action Buttons -->
                <div class="flex items-end justify-between gap-4">
                    <!-- Left side: Search Field -->
                    <div class="flex-1">
                        <label for="account-search" class="block text-sm font-medium text-gray-700 mb-1">${t('accounts.search')}</label>
                        <div class="relative">
                            <input type="text" 
                                   id="account-search" 
                                   placeholder="${t('accounts.searchPlaceholder')}" 
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                   oninput="handleAccountSearchInput(event)"
                                   onfocus="handleAccountSearchFocus()"
                                   onblur="handleAccountSearchBlur()">
                            <!-- Search Results Dropdown -->
                            <div id="account-search-results" 
                                 class="hidden absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                            </div>
                        </div>
                    </div>
                    
                    <!-- Right side: Action buttons -->
                    <div class="flex items-end space-x-2">
                        <button onclick="clearAccountSearch()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            ${t('accounts.clear')}
                        </button>
                        <button onclick="loadAccountsData()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            ${t('accounts.refresh')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Accounts List -->
        <div class="bg-white shadow rounded-lg overflow-hidden mt-3">
            <div class="px-4 py-5 sm:p-6">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-lg font-medium text-gray-900">Account Hierarchy</h2>
                </div>
                
                <!-- Table Header -->
                <div class="border-b border-gray-200 bg-gray-50 px-4 py-3">
                    <div class="flex items-center justify-between">
                        <div class="flex-1 font-medium text-sm text-gray-700">
                            ${t('accounts.name')}
                        </div>
                        <div class="flex items-center space-x-6 text-sm font-medium text-gray-700">
                            <div class="text-center w-20">${t('accounts.directUsers')}</div>
                            <div class="text-center w-20">${t('accounts.totalUsers')}</div>
                            <div class="w-24 text-center">${t('accounts.plan')}</div>
                        </div>
                    </div>
                </div>
                
                <!-- Accounts List -->
                <div id="accounts-list">
                    <div class="text-center py-8">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                        <p class="mt-2 text-sm text-gray-500">${t('accounts.loading')}</p>
                    </div>
                </div>
            </div>
        </div>
    `;

    updatePageTitle('Setup - Accounts');
    updateActiveNavigation('accounts');
    loadAccountsData();
}

/**
 * Auto-expand first level accounts
 */
export function autoExpandFirstLevelAccountsPage() {
    // Find top-level accounts (level 0) that have children
    const topLevelAccounts = document.querySelectorAll('.account-item[data-level="0"]');
    topLevelAccounts.forEach(accountDiv => {
        const accountId = accountDiv.getAttribute('data-account-id');
        if (accountId) {
            const expandBtn = document.getElementById(`expand-btn-${accountId}`);
            if (expandBtn) {
                // Click to expand
                expandBtn.click();
            }
        }
    });
}

/**
 * Load and display accounts data
 */
export async function loadAccountsData() {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const accountsList = document.getElementById('accounts-list');
        if (!accountsList) return;
        
        if (response && response.accounts_tree) {
            // Store accounts data for searching
            accountsTreeData = response.accounts_tree;
            flatAccountsList = buildFlatAccountsList(response.accounts_tree);
            
            accountsList.innerHTML = renderAccountsList(response.accounts_tree);
            
            // Auto-expand first level after data is loaded
            setTimeout(() => {
                autoExpandFirstLevelAccountsPage();
            }, 100);
        } else {
            accountsList.innerHTML = '<div class="text-center text-gray-500 py-8">No accounts found</div>';
        }
    } catch (error) {
        console.error('Failed to load accounts:', error);
        const accountsList = document.getElementById('accounts-list');
        if (accountsList) {
            accountsList.innerHTML = '<div class="text-center text-red-500 py-8">Failed to load accounts</div>';
        }
    }
}

/**
 * Render accounts as an expandable list view
 */
export function renderAccountsList(accounts, level = 0, parentId = null) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 32; // 32px per level for clear hierarchy
        
        // Hide children by default (level > 0)
        const isHidden = level > 0 ? 'hidden' : '';
        
        html += `
            <div class="account-item ${isHidden}" data-account-id="${accountId}" data-level="${level}" ${parentId ? `data-parent-id="${parentId}"` : ''}>
                <div class="flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b border-gray-100">
                    <!-- Account Name Column -->
                    <div class="flex items-center space-x-2 flex-1" style="padding-left: ${indentPadding}px;">
                        ${hasChildren ? `
                            <button onclick="toggleAccountItem('${accountId}')" 
                                    class="text-gray-400 hover:text-gray-600 transition-transform duration-200 flex-shrink-0"
                                    id="expand-btn-${accountId}">
                                <svg id="expand-icon-${accountId}" class="w-4 h-4 transform transition-transform" 
                                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                </svg>
                            </button>
                        ` : '<div class="w-4 flex-shrink-0"></div>'}
                        
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center space-x-2">
                                <button onclick="editAccount('${accountId}')" 
                                        class="text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors truncate">
                                    ${account.account_name}
                                </button>
                                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${getStatusBadgeClass(account.status)}">
                                    ${account.status}
                                </span>
                            </div>
                            <div class="flex items-center space-x-3 text-xs text-gray-500 mt-0.5">
                                <span>${account.account_type || 'Standard'}</span>
                                ${hasChildren ? `<span>• ${account.children.length} sub-account${account.children.length !== 1 ? 's' : ''}</span>` : ''}
                            </div>
                        </div>
                    </div>
                    
                    <!-- Data Columns -->
                    <div class="flex items-center space-x-6 flex-shrink-0">
                        <!-- Direct Users Column -->
                        <div class="text-center w-20">
                            <button onclick="viewAccountUsersFiltered('${accountId}', '${account.account_name.replace(/'/g, "\\'")}', 'direct')" 
                                    class="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors">
                                ${account.direct_users || 0}
                            </button>
                        </div>
                        
                        <!-- Total Users Column -->
                        <div class="text-center w-20">
                            <button onclick="viewAccountUsersFiltered('${accountId}', '${account.account_name.replace(/'/g, "\\'")}', 'total')" 
                                    class="text-sm font-medium text-green-600 hover:text-green-800 transition-colors">
                                ${account.current_users || 0}
                            </button>
                        </div>
                        
                        <!-- Plan Column -->
                        <div class="w-24 text-center text-sm text-gray-600">
                            ${account.subscription_plan || 'Standard'}
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        // Recursively render children
        if (hasChildren) {
            html += renderAccountsList(account.children, level + 1, accountId);
        }
    });
    
    return html;
}

/**
 * Toggle account item expansion
 */
export function toggleAccountItem(accountId) {
    const children = document.querySelectorAll(`[data-parent-id="${accountId}"]`);
    const expandIcon = document.getElementById(`expand-icon-${accountId}`);
    
    if (!expandIcon) return;
    
    const isExpanded = expandIcon.style.transform === 'rotate(90deg)';
    
    if (isExpanded) {
        // Collapse: hide direct children and all descendants
        children.forEach(child => {
            child.classList.add('hidden');
            const childId = child.getAttribute('data-account-id');
            if (childId) {
                collapseAccountItem(childId);
            }
        });
        expandIcon.style.transform = 'rotate(0deg)';
    } else {
        // Expand: show only direct children
        children.forEach(child => {
            child.classList.remove('hidden');
        });
        expandIcon.style.transform = 'rotate(90deg)';
    }
}

/**
 * Collapse an account item and all its descendants
 */
function collapseAccountItem(accountId) {
    const expandIcon = document.getElementById(`expand-icon-${accountId}`);
    if (expandIcon) {
        expandIcon.style.transform = 'rotate(0deg)';
    }
    
    const children = document.querySelectorAll(`[data-parent-id="${accountId}"]`);
    children.forEach(child => {
        child.classList.add('hidden');
        const childId = child.getAttribute('data-account-id');
        if (childId) {
            collapseAccountItem(childId);
        }
    });
}

/**
 * Helper function for status badge colors
 */
function getStatusBadgeClass(status) {
    const statusMap = {
        'Active': 'bg-green-100 text-green-800',
        'Inactive': 'bg-gray-100 text-gray-800',
        'Suspended': 'bg-red-100 text-red-800',
        'Trial': 'bg-blue-100 text-blue-800'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
}

/**
 * Show create account modal
 */
export function showCreateAccountModal() {
    const modal = document.getElementById('modal');
    if (!modal) return;

    // Check if user is SuperAdmin
    const currentUser = getCurrentUser();
    const isSuperAdmin = currentUser && currentUser.role === 'SuperAdmin';

    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-96 shadow-lg rounded-md bg-white">
                <div class="mt-3">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">${t('accounts.createTitle')}</h3>
                    <form id="create-account-form" class="space-y-4">
                        <div>
                            <label for="account-name" class="block text-sm font-medium text-gray-700">${t('accounts.name')} <span class="text-red-500">*</span></label>
                            <input type="text" id="account-name" name="account_name" required
                                   class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                   placeholder="${t('accounts.accountNamePlaceholder')}">
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">${t('accounts.parent')}</label>
                            <div class="text-xs text-gray-500 mb-2">${t('accounts.parentAccountDesc')}</div>
                            <div id="parent-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-64 overflow-y-auto bg-white">
                                <div class="text-gray-400 text-sm">${t('accounts.loading')}</div>
                            </div>
                            <input type="hidden" id="parent-account-id" name="parent_id">
                        </div>
                        ${isSuperAdmin ? `
                        <div>
                            <label for="account-type" class="block text-sm font-medium text-gray-700">${t('accounts.accountType')}</label>
                            <select id="account-type" name="account_type"
                                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <option value="Enterprise">Enterprise</option>
                                <option value="Business">Business</option>
                                <option value="Standard" selected>Standard</option>
                            </select>
                        </div>
                        <div>
                            <label for="account-status" class="block text-sm font-medium text-gray-700">${t('common.status')}</label>
                            <select id="account-status" name="status"
                                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                <option value="Active" selected>${t('common.active')}</option>
                                <option value="Inactive">${t('common.inactive')}</option>
                                <option value="Suspended">${t('accounts.statusSuspended')}</option>
                            </select>
                        </div>
                        <div>
                            <label for="max-users" class="block text-sm font-medium text-gray-700">${t('accounts.maxUsersOptional')}</label>
                            <input type="number" id="max-users" name="max_users" min="1"
                                   class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                   placeholder="${t('accounts.maxUsersPlaceholder')}">
                        </div>
                        <div id="tasks-feature-container" class="hidden border-t border-gray-200 pt-4 space-y-3">
                            <label class="flex items-center cursor-pointer">
                                <input type="checkbox" id="account-enable-tasks" name="enable_tasks"
                                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                <span class="ml-2 text-sm font-medium text-gray-700">Enable Tasks feature</span>
                            </label>
                            <label class="flex items-center cursor-pointer">
                                <input type="checkbox" id="account-enable-timetracking" name="enable_timetracking" checked
                                       onchange="window.toggleAccountTimeTracking && window.toggleAccountTimeTracking('account')"
                                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                <span class="ml-2 text-sm font-medium text-gray-700">${t('accounts.enableTimeTracking')}</span>
                            </label>
                            <div id="account-timetracking-subfeatures" class="ml-6 space-y-2">
                                <label class="flex items-center cursor-pointer">
                                    <input type="checkbox" id="account-enable-smarttime" name="enable_smarttime" checked
                                           class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                    <span class="ml-2 text-sm text-gray-700">${t('accounts.enableSmartTime')}</span>
                                </label>
                                <label class="flex items-center cursor-pointer">
                                    <input type="checkbox" id="account-enable-timebudget" name="enable_timebudget" checked
                                           class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                    <span class="ml-2 text-sm text-gray-700">${t('accounts.enableTimeBudget')}</span>
                                </label>
                            </div>
                            <p class="text-xs text-gray-500 mt-1">${t('accounts.topLevelFeaturesHint')}</p>
                        </div>
                        ` : ''}
                    </form>
                    <div class="flex items-center justify-end space-x-3 mt-6">
                        <button onclick="closeModal()" 
                                class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                            ${t('common.cancel')}
                        </button>
                        <button onclick="createAccount()" 
                                class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                            ${t('accounts.createButton')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    modal.classList.remove('hidden');
    
    // Load parent account hierarchy tree
    modals.loadParentAccountTree();
}

// Re-export modal functions
export const loadParentAccountTree = modals.loadParentAccountTree;
export const renderParentAccountTree = modals.renderParentAccountTree;
export const toggleParentAccountTreeItem = modals.toggleParentAccountTreeItem;
export const selectParentAccount = modals.selectParentAccount;
export const createAccount = modals.createAccount;
export const editAccount = modals.editAccount;
export const toggleEditParentAccountTreeItem = modals.toggleEditParentAccountTreeItem;
export const selectEditParentAccount = modals.selectEditParentAccount;
export const updateAccount = modals.updateAccount;

/**
 * Build a hierarchy map from accounts tree for quick lookup
 */
export function buildAccountHierarchyMap(accounts) {
    const map = {};
    
    function processAccount(account) {
        const accountId = account._id || account.id;
        const childIds = [];
        
        if (account.children && account.children.length > 0) {
            account.children.forEach(child => {
                const childId = child._id || child.id;
                childIds.push(childId);
                processAccount(child); // Recursively process children
            });
        }
        
        map[accountId] = {
            id: accountId,
            name: account.account_name || account.name,
            children: childIds
        };
    }
    
    accounts.forEach(account => processAccount(account));
    return map;
}

/**
 * Get all account IDs including descendants from a hierarchy map
 */
export function getAllAccountIdsIncludingDescendants(accountId, hierarchyMap) {
    const result = [accountId];
    
    function addDescendants(id) {
        const account = hierarchyMap[id];
        if (account && account.children.length > 0) {
            account.children.forEach(childId => {
                result.push(childId);
                addDescendants(childId);
            });
        }
    }
    
    addDescendants(accountId);
    return result;
}

/**
 * Build a flat list of all accounts for searching
 */
function buildFlatAccountsList(accounts, parentPath = [], result = []) {
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name;
        const currentPath = [...parentPath, accountName];
        
        result.push({
            id: accountId,
            name: accountName,
            path: currentPath,
            pathString: currentPath.join(' > '),
            data: account
        });
        
        if (account.children && account.children.length > 0) {
            buildFlatAccountsList(account.children, currentPath, result);
        }
    });
    
    return result;
}

/**
 * Handle account search input
 */
export function handleAccountSearchInput(event) {
    const searchTerm = event.target.value.trim().toLowerCase();
    
    // Clear existing timeout
    if (searchTimeout) {
        clearTimeout(searchTimeout);
    }
    
    // Debounce search
    searchTimeout = setTimeout(() => {
        performAccountSearch(searchTerm);
    }, 300);
}

/**
 * Perform account search and show results
 */
function performAccountSearch(searchTerm) {
    const resultsContainer = document.getElementById('account-search-results');
    if (!resultsContainer) return;
    
    if (!searchTerm || searchTerm.length < 2) {
        resultsContainer.classList.add('hidden');
        resultsContainer.innerHTML = '';
        return;
    }
    
    // Filter accounts by name
    const matches = flatAccountsList.filter(account => 
        account.name.toLowerCase().includes(searchTerm)
    );
    
    if (matches.length === 0) {
        resultsContainer.innerHTML = `
            <div class="px-3 py-2 text-sm text-gray-500">
                No accounts found
            </div>
        `;
        resultsContainer.classList.remove('hidden');
        return;
    }
    
    // Limit results to 10
    const displayMatches = matches.slice(0, 10);
    
    const resultsHtml = displayMatches.map(account => `
        <button type="button"
                onclick="selectAccountFromSearch('${account.id}')"
                class="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none">
            <div class="text-sm font-medium text-gray-900">${account.name}</div>
            <div class="text-xs text-gray-500">${account.pathString}</div>
        </button>
    `).join('');
    
    resultsContainer.innerHTML = resultsHtml;
    resultsContainer.classList.remove('hidden');
}

/**
 * Handle search field focus
 */
export function handleAccountSearchFocus() {
    const searchInput = document.getElementById('account-search');
    const searchTerm = searchInput?.value.trim().toLowerCase();
    
    if (searchTerm && searchTerm.length >= 2) {
        performAccountSearch(searchTerm);
    }
}

/**
 * Handle search field blur
 */
export function handleAccountSearchBlur() {
    // Delay to allow click on results
    setTimeout(() => {
        const resultsContainer = document.getElementById('account-search-results');
        if (resultsContainer) {
            resultsContainer.classList.add('hidden');
        }
    }, 200);
}

/**
 * Select an account from search results
 */
export function selectAccountFromSearch(accountId) {
    // Hide search results
    const resultsContainer = document.getElementById('account-search-results');
    if (resultsContainer) {
        resultsContainer.classList.add('hidden');
    }
    
    // Find the account in the flat list
    const account = flatAccountsList.find(acc => acc.id === accountId);
    if (!account) return;
    
    // Expand the hierarchy to show this account
    expandToAccount(accountId, account.path);
    
    // Highlight the account
    highlightAccount(accountId);
    
    // Scroll to the account
    setTimeout(() => {
        const accountElement = document.querySelector(`[data-account-id="${accountId}"]`);
        if (accountElement) {
            accountElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, 300);
}

/**
 * Expand hierarchy to show a specific account
 */
function expandToAccount(accountId, path) {
    // First, find all parent account IDs by traversing the tree
    const parentIds = findParentAccountIds(accountId, accountsTreeData);
    
    // Expand all parents
    parentIds.forEach(parentId => {
        const children = document.querySelectorAll(`[data-parent-id="${parentId}"]`);
        const expandIcon = document.getElementById(`expand-icon-${parentId}`);
        
        if (expandIcon && expandIcon.style.transform !== 'rotate(90deg)') {
            children.forEach(child => {
                child.classList.remove('hidden');
            });
            expandIcon.style.transform = 'rotate(90deg)';
        }
    });
}

/**
 * Find all parent IDs for a given account
 */
function findParentAccountIds(targetId, accounts, parents = []) {
    for (const account of accounts) {
        const accountId = account._id || account.id;
        
        if (accountId === targetId) {
            return parents;
        }
        
        if (account.children && account.children.length > 0) {
            const result = findParentAccountIds(targetId, account.children, [...parents, accountId]);
            if (result) {
                return result;
            }
        }
    }
    
    return null;
}

/**
 * Highlight an account temporarily
 */
function highlightAccount(accountId) {
    const accountElement = document.querySelector(`[data-account-id="${accountId}"]`);
    if (!accountElement) return;
    
    // Add highlight class
    const innerDiv = accountElement.querySelector('div');
    if (innerDiv) {
        innerDiv.classList.add('bg-yellow-100');
        
        // Remove highlight after 2 seconds
        setTimeout(() => {
            innerDiv.classList.remove('bg-yellow-100');
        }, 2000);
    }
}

/**
 * Clear account search
 */
export function clearAccountSearch() {
    const searchInput = document.getElementById('account-search');
    const resultsContainer = document.getElementById('account-search-results');
    
    if (searchInput) {
        searchInput.value = '';
    }
    
    if (resultsContainer) {
        resultsContainer.classList.add('hidden');
        resultsContainer.innerHTML = '';
    }
}

// Continue in next message due to length...
export default {
    showAccounts,
    loadAccountsData,
    renderAccountsList,
    toggleAccountItem,
    autoExpandFirstLevelAccountsPage,
    showCreateAccountModal,
    buildAccountHierarchyMap,
    getAllAccountIdsIncludingDescendants,
    handleAccountSearchInput,
    handleAccountSearchFocus,
    handleAccountSearchBlur,
    selectAccountFromSearch,
    clearAccountSearch,
    ...modals.default
};
